import { Pencil, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CustomFooterItem } from '../types/footer';
import { getImageUrl } from '../utils';
import { getAllGLobalFooters } from '../utils/requests';

interface FooterSelectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (footer: CustomFooterItem) => void;
    onEdit: (footerId: string) => void;
    selectedFooterId?: string;
}

const GlobalFooterSelectModal: React.FC<FooterSelectModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    onEdit,
    selectedFooterId
}) => {
    // State management
    const [footers, setFooters] = useState<CustomFooterItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    // Pagination settings
    const itemsPerPage: number = 5;

    // Load footers on modal open
    useEffect(() => {
        if (isOpen) {
            const loadFooters = async (): Promise<void> => {
                setIsLoading(true);
                try {
                    const response = await getAllGLobalFooters({
                        page: currentPage,
                        perPage: itemsPerPage
                    });
                    setFooters(response.data);
                    setTotalPages(Math.ceil(response.page?.totalIndex / itemsPerPage));
                } catch (error) {
                    console.error('Error fetching footers:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            loadFooters();
        }
    }, [isOpen, currentPage]);

    // Handle footer selection
    const handleSelect = (footer: CustomFooterItem): void => {
        onSelect(footer);
    };

    // Handle footer edit
    const handleEdit = (footerId: string, e: React.MouseEvent): void => {
        e.stopPropagation(); // Prevent triggering the row click (select)
        onEdit(footerId);
    };

    // If modal is closed, don't render anything
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Modal footer */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">Select Global Footer</h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal body */}
                <div className="flex-grow overflow-y-auto">
                    {isLoading ? (
                        <div className="p-8 text-center">Loading footers...</div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {footers.map((footer) => (
                                <div
                                    key={footer._id}
                                    onClick={() => handleSelect(footer)}
                                    className={`p-4 flex items-center cursor-pointer hover:bg-gray-50 transition duration-300`}
                                >
                                    {/* Image */}
                                    <div className="w-[300px] h-[100px] mr-4 overflow-hidden rounded border">
                                        {footer.images && (
                                            <img
                                                src={getImageUrl(footer.images)}
                                                alt={footer.name}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-grow">
                                        <h3 className="font-medium">{footer.name}</h3>
                                        <p className="text-sm text-gray-500">{footer.description}</p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={(e) => handleEdit(footer._id, e)}
                                            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                                            title="Edit footer"
                                        >
                                            <Pencil className="w-5 h-5" />
                                        </button>

                                        {/* {footer.isSelected ? (
                                            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                        ) : (
                                        )} */}
                                        <div className="w-6 h-6 rounded-full border-2 border-gray-300"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="p-4 border-t">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 rounded ${currentPage === totalPages
                                        ? 'bg-gray-100 text-gray-400'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal footer */}
                <div className="p-4 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Usage example component
const FooterSelectModalExample = ({
    onSelect,
    onSkip
}: {
    onSelect: (footer: CustomFooterItem) => void;
    onSkip: () => void;
}) => {
    const [selectedFooterId, setSelectedFooterId] = useState<string>();

    const handleSelect = (footer: CustomFooterItem): void => {
        setSelectedFooterId(footer._id);
        onSelect(footer);
    };

    const handleEdit = (footerId: string): void => {
        console.log(`Edit footer with ID: ${footerId}`);
        // Here you would typically redirect to an edit page
        // or open another modal for editing
    };

    const handleClose = (): void => {
        onSkip();
    };

    return (
        <GlobalFooterSelectModal
            isOpen={true}
            onClose={handleClose}
            onSelect={handleSelect}
            onEdit={handleEdit}
            selectedFooterId={selectedFooterId}
        />
    );
};

export default FooterSelectModalExample;
